import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildFilterChips } from '../../../components/filters/filterChips';
import { FilterCriteriaTokens } from '../../../components/filters/FilterCriteriaTokens';
import type { FilterPanelProps } from '../../../components/filters/filterTypes';
import { PRIMARY_ALPHA } from '../../../components/ui/columnConfigurator/ColumnConfiguratorDrawer.styles';
import { DrawerHeader } from '../../../components/ui/drawer/DrawerHeader';
import { DrawerScrollBody } from '../../../components/ui/drawer/DrawerScrollBody';
import { DrawerSearch } from '../../../components/ui/drawer/DrawerSearch';
import { SidePanelDrawer } from '../../../components/ui/drawer/SidePanelDrawer';
import { FilterPanel } from '../../../components/ui/filterPanel/FilterPanel';
import type { FilterSection } from '../../../components/ui/filterPanel/filterPanel.types';
import type { EmployeeRow } from '../employeeTypes';
import { EMPLOYEE_ADVANCED_FILTER_KEYS, EMPLOYEE_FILTER_SECTIONS } from './employeeFilterSections';

const FILTER_DRAWER_TITLE_ID = 'employee-filter-drawer-title';

interface EmployeeFilterDrawerProps extends FilterPanelProps<EmployeeRow> {
  open: boolean;
  onClose: () => void;
  /** Commit the edited (draft) filters onto the applied state. */
  onApply: () => void;
  /** Clear all edited filters. */
  onReset: () => void;
  /** True when the draft differs from the applied filters. */
  hasUnapplied: boolean;
}

/**
 * Advanced-filters panel presented as a right-side drawer, mirroring the column
 * configurator's shell (`DrawerHeader` / `DrawerSearch` / shared token CSS). The
 * body reuses the generic `FilterPanel` (single-column, header hidden); the meta
 * area shows the active-criteria count + removable chips.
 */
export function EmployeeFilterDrawer({
  open,
  onClose,
  onApply,
  onReset,
  hasUnapplied,
  allColumns,
  filterState,
  onFilterChange,
  onFilterClear,
}: EmployeeFilterDrawerProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const chips = useMemo(
    () => buildFilterChips(EMPLOYEE_ADVANCED_FILTER_KEYS, allColumns, filterState, t),
    [allColumns, filterState, t],
  );

  // Restrict the shown criteria to those whose label matches the search term.
  const filteredSections: FilterSection<EmployeeRow>[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return EMPLOYEE_FILTER_SECTIONS;
    return EMPLOYEE_FILTER_SECTIONS.map(s => ({
      ...s,
      fields: s.fields.filter(k => {
        const col = allColumns.find(c => c.key === k);
        return col ? t(col.labelKey).toLowerCase().includes(q) : false;
      }),
    })).filter(s => s.fields.length > 0);
  }, [search, allColumns, t]);

  return (
    <SidePanelDrawer open={open} onClose={onClose} ariaLabelledBy={FILTER_DRAWER_TITLE_ID}>
      <DrawerHeader
        onCancel={onClose}
        titleId={FILTER_DRAWER_TITLE_ID}
        title={t('employeeList.filters.advancedFilters')}
        icon={<TuneIcon sx={{ fontSize: 'var(--icon-header)', color: 'white' }} />}
      />

      {/* ── Meta: active-criteria count + removable chips + operator note ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: '1px solid var(--gray-200)',
          bgcolor: alpha(theme.palette.primary.main, PRIMARY_ALPHA.sectionBg),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: chips.length > 0 ? 1 : 0,
          }}
        >
          <Typography
            sx={{
              color: 'var(--gray-600)',
              fontWeight: theme.typography.fontWeightMedium,
              fontSize: 'var(--font-counter-label)',
            }}
          >
            {t('employeeList.filters.activeCriteria')}
          </Typography>
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: theme.typography.fontWeightBold,
              fontSize: 'var(--font-counter-value)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {chips.length}
          </Typography>
        </Box>

        <FilterCriteriaTokens
          chips={chips}
          onRemove={onFilterClear}
          removeLabel={t('employeeList.filters.removeCriterion')}
        />

        <Box sx={{ display: 'flex', gap: 0.75, mt: chips.length > 0 ? 1.25 : 1 }}>
          <InfoOutlinedIcon
            sx={{ fontSize: 'var(--icon-action)', color: 'var(--gray-500)', flexShrink: 0, mt: '1px' }}
          />
          <Typography sx={{ fontSize: 'var(--font-warning)', color: 'var(--gray-600)', lineHeight: 1.35 }}>
            {t('employeeList.filters.operatorNote')}
          </Typography>
        </Box>
      </Box>

      <DrawerSearch
        search={search}
        onChange={setSearch}
        onClear={() => setSearch('')}
        placeholder={t('employeeList.filters.searchCriteria')}
      />

      {/* ── Body: the shared FilterPanel, single column, own header hidden ── */}
      <DrawerScrollBody sx={{ px: 2, py: 1.5, bgcolor: 'var(--gray-50)' }}>
        <FilterPanel<EmployeeRow>
          allColumns={allColumns}
          filterState={filterState}
          onFilterChange={onFilterChange}
          onFilterClear={onFilterClear}
          titleKey="employeeList.filters.advancedFilters"
          sections={filteredSections}
          gridColumns="1fr"
          hideHeader
          paperSx={{
            border: 'none',
            borderRadius: 0,
            p: 0,
            m: 0,
            bgcolor: 'transparent',
            boxShadow: 'none',
          }}
        />
      </DrawerScrollBody>

      {/* ── Footer: Réinitialiser (secondary) + Appliquer (primary) ── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          flexShrink: 0,
          borderTop: '1px solid var(--gray-200)',
          bgcolor: 'var(--white-97)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onReset}
          disabled={chips.length === 0 && !hasUnapplied}
          sx={{ borderRadius: theme.borderRadius.sm, textTransform: 'none', fontSize: 'var(--font-button)' }}
        >
          {t('employeeList.filters.reset')}
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          sx={{ borderRadius: theme.borderRadius.sm, textTransform: 'none', fontSize: 'var(--font-button)' }}
        >
          {t('employeeList.filters.apply')}
        </Button>
      </Box>
    </SidePanelDrawer>
  );
}
